// File: frontend/src/api/activity.ts
import { API_BASE_URL } from '@/config/apiConfig';
import { ApiError } from './auth';
import { createApiClient } from './client';
import { LeagueActivityRead, PaginatedLeagueActivityResponse, MarkReadPayload, ChatPostPayload } from './schemas'; // We'll add these to schemas.ts next

const apiClient = createApiClient({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

export type ActivityCategory = 'league' | 'new_players';

export const fetchLeagueActivity = async (
  leagueId: number,
  token: string,
  filter: string | null,
  skip: number = 0,
  limit: number = 30, // Fetch 30 messages per page
  category: ActivityCategory | null = null
): Promise<PaginatedLeagueActivityResponse> => {
  const params = new URLSearchParams({
    skip: String(skip),
    limit: String(limit),
  });
  if (filter && filter !== 'all') {
    params.append('activity_type_filter', filter);
  }
  if (category) {
    params.append('category', category);
  }

  const url = `${API_BASE_URL}/leagues/${leagueId}/activities?${params.toString()}`;
  try {
    const response = await apiClient.get<PaginatedLeagueActivityResponse>(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    throw new ApiError(error.response?.data?.detail || 'Failed to fetch activity', error.response?.status);
  }
};

export const postChatMessage = async (
  leagueId: number,
  token: string,
  payload: ChatPostPayload
): Promise<LeagueActivityRead> => {
  const url = `${API_BASE_URL}/leagues/${leagueId}/activities`;
  try {
    const response = await apiClient.post<LeagueActivityRead>(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    throw new ApiError(error.response?.data?.detail || 'Failed to post message', error.response?.status);
  }
};

export const markActivityAsRead = async (
  leagueId: number,
  token: string,
  payload: MarkReadPayload
): Promise<void> => {
  const url = `${API_BASE_URL}/leagues/${leagueId}/activities/mark-read`;
  try {
    await apiClient.post(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (error: any)
  {
    // We can often fail silently on this one
    console.error("Failed to mark activity as read:", error);
  }
};

export interface UnreadActivityCounts {
    unread_count: number;
    new_players_unread_count?: number;
}

export const fetchUnreadActivityCount = async (
    leagueId: number,
    token: string
): Promise<UnreadActivityCounts> => {
    const url = `${API_BASE_URL}/leagues/${leagueId}/activities/unread-count`;
    const response = await apiClient.get<UnreadActivityCounts>(url, {
        headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
};

export type PlayerLeagueHistoryKind =
    | 'DRAFTED' | 'OFFLINE_DRAFTED' | 'UNDRAFTED' | 'NOT_DRAFTED_YET'
    | 'DROPPED' | 'FREE_AGENT_ADD' | 'WAIVER_ADD' | 'TRADED';

/** One completed roster move for a player in a league, or his draft origin. */
export interface PlayerLeagueHistoryEvent {
    id: string;
    kind: PlayerLeagueHistoryKind;
    text: string;
    occurred_at: string | null;
}

/** Newest move first; the draft origin (or Undrafted) comes last. Read-only:
 *  never advances the league Activity read watermarks. */
export const fetchPlayerLeagueHistory = async (
    leagueId: number,
    playerId: number,
    token: string,
    signal?: AbortSignal,
): Promise<PlayerLeagueHistoryEvent[]> => {
    const url = `${API_BASE_URL}/leagues/${leagueId}/players/${playerId}/history`;
    const response = await apiClient.get<{ events: PlayerLeagueHistoryEvent[] }>(url, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
    });
    return response.data.events;
};
